import type { QuoteSummaryVehicle } from 'api/quote-marketplace';

export interface ParsedVehicleData {
  totalPremium: number | null;
  coverageBreakdownHtml: string | null;
  discountsHtml: string | null;
  ratingFactorsHtml: string | null;
}

interface VehicleTable {
  modelKey: string;
  html: string;
  premium: number | null;
}

/** Reduce a make and model to a comparable key, ignoring spacing and case. */
function normalizeKey(value: string | null | undefined): string {
  return (value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
}

function extractPremiumFromHeader(headerText: string): number | null {
  const match = headerText.match(/\(Total \$?([\d,]+\.?\d*)\)/i);
  return match ? parseFloat(match[1].replace(/,/g, '')) : null;
}

/**
 * Pull "TOYOTA COROLLA SE 4DR" out of a Vehicle table header.
 * Returns null when the header is not a vehicle table for `section`.
 */
function parseVehicleHeader(
  headerText: string,
  section: string,
): string | null {
  const match = headerText.match(/^(.+?)\s*-\s*Vehicle\s*\d+\s*:\s*(.+)$/i);
  if (!match) return null;
  if (match[1].trim().toLowerCase() !== section.toLowerCase()) return null;
  return normalizeKey(match[2]);
}

function parseTables(html: string | null, section: string): VehicleTable[] {
  if (!html) return [];

  const container = document.createElement('div');
  container.innerHTML = html;

  const tables: VehicleTable[] = [];
  container.querySelectorAll('table').forEach((table) => {
    const firstRow = table.querySelector('tr:first-child');
    if (!firstRow) return;

    const headerCell =
      firstRow.querySelector('td:first-child') ||
      firstRow.querySelector('th:first-child');
    if (!headerCell) return;

    const modelKey = parseVehicleHeader(headerCell.textContent || '', section);
    if (modelKey === null) return;

    tables.push({
      modelKey,
      html: table.outerHTML,
      premium: extractPremiumFromHeader(firstRow.textContent || ''),
    });
  });

  return tables;
}

/**
 * Match `tables` to `vehicles`, returning an array parallel to `vehicles`.
 *
 * Acturis lists risks in its own order and numbers the "Vehicle N" tables from
 * that, so the header number does not identify a vehicle. The headers carry
 * only a make and model, so two vehicles of the same model can only fall back
 * to the order Acturis listed them in.
 */
function matchTables(
  vehicles: QuoteSummaryVehicle[],
  tables: VehicleTable[],
): (VehicleTable | null)[] {
  const matched: (VehicleTable | null)[] = vehicles.map(() => null);
  const used = new Set<number>();

  const keyFor = (vehicle: QuoteSummaryVehicle) =>
    normalizeKey(`${vehicle.make} ${vehicle.model}`);

  vehicles.forEach((vehicle, index) => {
    const key = keyFor(vehicle);
    if (!key) return;

    const position = tables.findIndex(
      (table, at) => !used.has(at) && table.modelKey === key,
    );
    if (position !== -1) {
      matched[index] = tables[position];
      used.add(position);
    }
  });

  // An insurer may pad the model out ("2020 TOYOTA COROLLA SE 4DR"), so allow
  // containment where it still singles one table out.
  vehicles.forEach((vehicle, index) => {
    if (matched[index]) return;

    const key = keyFor(vehicle);
    if (!key) return;

    const hits = tables
      .map((table, at) => ({ table, at }))
      .filter(
        ({ table, at }) =>
          !used.has(at) &&
          table.modelKey &&
          (table.modelKey.includes(key) || key.includes(table.modelKey)),
      );
    if (hits.length === 1) {
      matched[index] = hits[0].table;
      used.add(hits[0].at);
    }
  });

  return matched;
}

export function getVehiclesParsedData(
  vehicles: QuoteSummaryVehicle[],
  coverageHtml: string | null,
  discountsHtml: string | null,
  ratingFactorsHtml: string | null,
): ParsedVehicleData[] {
  const coverage = matchTables(
    vehicles,
    parseTables(coverageHtml, 'Coverage Breakdown'),
  );
  const discounts = matchTables(
    vehicles,
    parseTables(discountsHtml, 'Discounts'),
  );
  const ratingFactors = matchTables(
    vehicles,
    parseTables(ratingFactorsHtml, 'Rating Factors'),
  );

  return vehicles.map((_, index) => ({
    totalPremium: coverage[index]?.premium ?? null,
    coverageBreakdownHtml: coverage[index]?.html ?? null,
    discountsHtml: discounts[index]?.html ?? null,
    ratingFactorsHtml: ratingFactors[index]?.html ?? null,
  }));
}
